/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, signal,
} from '@angular/core';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { FeedStoreService } from '../services/feed-store.service';
import { createFailureAwareStore } from '../shared/failure-aware-store';
import { focusNewItem } from '../shared/focus-new-item';
import { pageWindow } from '../shared/page-window';
import { itemCommentCount } from '../common/community/feed/community-item';
import { toPreviewHtml } from '../common/community/feed/preview-html';
import { LikeService } from '../services/like.service';
import { openLikeList } from '../common/community/feed/like-list/community-like-list-dialog.component';

export type ForumsTab = 'recent-replies' | 'newly-created' | 'most-replies';

export const FORUMS_TABS: { value: ForumsTab; label: string; sort: string }[] = [
  { value: 'recent-replies', label: 'Recent replies', sort: '-last_commented_at' },
  { value: 'newly-created', label: 'Newly created', sort: '-published_at' },
  { value: 'most-replies', label: 'Most replies', sort: '-comment_count,-published_at' },
];

const PER_PAGE = 20;

// /forums and /forums/category/:categoryId in the 2026 design (FORUMS_ARTICLES_REDESIGN.html).
// The index keeps DiscussionCtrl's data: ?tab picks the sort, ?category_id filters, ?topic_page pages, 20 per page.
// Every change is a new URL, so back / forward and deep links work. On the `forums` state those params are dynamic:
// a change reloads only the topic list, and the hero, featured topics and focus stay put. With a category it's that
// category's page: sticky topics first and load more, as DiscussionCategoryCtrl did.
@Component({
  selector: 'ut-forums-page',
  templateUrl: './forums-page.component.html',
  styleUrls: ['./forums-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ForumsPageComponent implements OnInit, OnDestroy {
  // The root state's `user`: null when logged out.
  @Input() user: any;
  // The index's CMS header and random featured topics.
  @Input() headerData: any;
  @Input() featured: any[] = [];
  // The `discussioncategory` state's category.
  @Input() category: any;

  readonly tabs = FORUMS_TABS;
  readonly replyCount = itemCommentCount;
  // Title widths (%) for the loading rows, varied so they read as different topics.
  readonly loadingRows = [62, 44, 71, 38, 55, 48];

  get hasFeatured(): boolean {
    return !this.category && !!this.featured?.length;
  }

  tab: ForumsTab = 'recent-replies';
  categoryId: number | null = null;
  categories = signal<any[]>([]);
  topics = signal<any[] | null>(null);
  failed = signal(false);
  page = 1;
  totalPages = 1;
  // The category page's FeedStore.
  store: any;

  private removeTransitionHook: () => void = () => {};
  // Answers to earlier params are dropped when tabs or pages change quickly.
  private request = 0;
  // Set when a pager link was used: Previous / Next can disappear, taking focus with them.
  private paged = false;
  // Topic id → preview HTML, so each body goes through uMarkdown once.
  private previews = new Map<number, string>();
  // Topics with a like / unlike on its way, so a quick second click can't send the opposite request as well.
  private liking = new Set<number>();

  constructor(
    @Inject('DiscussionService') private DiscussionService: any,
    @Inject('DiscussionCategoryService') private DiscussionCategoryService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('$state') private $state: any,
    @Inject('$transitions') private $transitions: any,
    @Inject('$filter') private $filter: any,
    @Inject('$analytics') private $analytics: any,
    @Inject('AlertService') private AlertService: any,
    private likeService: LikeService,
    private feedStore: FeedStoreService,
    private dialogService: DialogService,
    readonly cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.DiscussionCategoryService.allForSelect().then((categories) => this.categories.set(categories));
    if (this.category) {
      this.store = createFailureAwareStore(
        this.feedStore,
        (params) => this.DiscussionService.feed(params),
        { filter: { category_id: this.category.id }, sort: '-sticky,-published_at' },
      );
      this.loadMore();
      return;
    }
    this.applyParams(this.$state.params);
    this.loadPage();
    this.removeTransitionHook = this.$transitions.onSuccess({ to: 'forums' }, (transition) => {
      if (this.applyParams(transition.params())) this.loadPage();
    });
  }

  ngOnDestroy(): void {
    this.removeTransitionHook();
  }

  get status(): string {
    if (this.failed()) return '';
    const topics = this.rows;
    if (!topics) return 'Loading topics';
    return this.category ? `${topics.length} topics shown` : `Page ${this.page} of ${this.totalPages}, ${topics.length} topics`;
  }

  // Reads ?tab, ?category_id and ?topic_page; true when they changed what's listed.
  private applyParams(params: any): boolean {
    const tab = FORUMS_TABS.some((t) => t.value === params.tab) ? params.tab : 'recent-replies';
    const categoryId = params.category_id ? Number(params.category_id) : null;
    const page = Number(params.topic_page) || 1;
    const changed = tab !== this.tab || categoryId !== this.categoryId || page !== this.page;
    this.tab = tab;
    this.categoryId = categoryId;
    this.page = page;
    return changed;
  }

  get canCreate(): boolean {
    return !!this.user?.current_user_permissions?.create_topic;
  }

  get pages(): number[] {
    return pageWindow(this.page, this.totalPages);
  }

  // CSS url() for the hero, falling back to the forums covers the legacy page uses. The small one is a tiny
  // (80px, ~10KB) preview that shows, blurry, under the full image until it loads.
  cover(src: string | undefined, fallback: string): string {
    return `url('${src || fallback}')`;
  }

  // The body as preview HTML (links and emoji only, as in the community feed), for the four-line preview.
  preview(topic: any): string {
    if (!this.previews.has(topic.id)) {
      this.previews.set(topic.id, toPreviewHtml(this.$filter('uMarkdown')(topic.content || ''), this.$filter).trim());
    }
    return this.previews.get(topic.id);
  }

  // As the legacy like button: light testers get their prompt, and the count follows the server's answer.
  toggleLike(topic: any): void {
    if (this.liking.has(topic.id) || this.LightTesterErrorService.show() || !topic.current_user_permissions?.like) return;
    this.liking.add(topic.id);
    const like = !topic.likes?.liked_by_current_user;
    this.$analytics.eventTrack(like ? 'like' : 'unlike', { category: 'Likes', label: topic.type });
    (like ? this.likeService.like(topic.type, topic.id) : this.likeService.unlike(topic.type, topic.id)).then((res) => {
      topic.likes = { ...topic.likes, ...res.data.likes };
      this.likeService.changed.next(topic);
    }, () => this.AlertService.addDanger('Request failed')).finally(() => {
      this.liking.delete(topic.id);
      this.cdr.markForCheck();
    });
  }

  showLikes(topic: any): void {
    openLikeList(this.dialogService, topic, this.user);
  }

  // As the topic page's action bar: reporting isn't offered on your own topic.
  canReport(topic: any): boolean {
    return !!topic.current_user_permissions?.flag && topic.user?.id !== this.user?.id;
  }

  get newDiscussionHref(): string {
    return this.$state.href('newDiscussion', { categoryId: this.category?.id });
  }

  // Light testers can't create topics. A uiSref would navigate regardless of preventDefault, so plain clicks go
  // through $state; modified clicks keep the browser's new tab / window behavior.
  newDiscussion(event: MouseEvent): void {
    if (this.LightTesterErrorService.show()) {
      event.preventDefault();
      return;
    }
    if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      event.preventDefault();
      this.$state.go('newDiscussion', { categoryId: this.category?.id });
    }
  }

  filterBy(categoryId: number | null): void {
    this.$state.go('forums', { tab: this.tab, topic_page: 1, category_id: categoryId });
  }

  loadPage(): void {
    const request = ++this.request;
    this.failed.set(false);
    this.topics.set(null);
    this.cdr.markForCheck();
    const { sort } = FORUMS_TABS.find((t) => t.value === this.tab);
    // Without a category the filter must be empty: the API reads `category_id: null` as "no category" and
    // returns nothing.
    const filter = this.categoryId ? { category_id: this.categoryId } : {};
    this.DiscussionService.feed({
      page: this.page, sort, filter, per_page: PER_PAGE,
    }).then((res) => {
      if (request !== this.request) return;
      this.totalPages = Number(res.headers('X-Total-Pages')) || 1;
      this.topics.set(res.data);
      this.keepPagerFocus();
    }, () => {
      if (request !== this.request) return;
      this.topics.set([]);
      this.failed.set(true);
    });
  }

  markPaged(): void {
    this.paged = true;
  }

  // The links that were used stay mounted, so they keep focus; only a Previous / Next that's gone (first or last
  // page) hands it to the current page link.
  private keepPagerFocus(): void {
    if (!this.paged) return;
    this.paged = false;
    setTimeout(() => {
      if (document.activeElement === document.body) {
        this.el.nativeElement.querySelector<HTMLElement>('.pager__page[aria-current]')?.focus();
      }
    });
  }

  // The topics on the index page, or the category page's loaded topics.
  get rows(): any[] | null {
    if (!this.category) return this.topics();
    return this.store.feedItems.length || !this.store.isLoading ? this.store.feedItems : null;
  }

  loadMore(): void {
    const next = this.store.feedItems.length;
    this.cdr.markForCheck();
    this.store.loadMore().then(() => {
      this.cdr.markForCheck();
      const topic = this.store.feedItems[next];
      if (next && topic) focusNewItem(this.el.nativeElement, `#topic_${topic.id}`);
    });
  }
}
